import React, { useMemo, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import SkeletonLoader from "../Loaders/SkeletonLoader";
import { useAppSelector } from "../../hooks";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { DOCUMENT_VIEW_FAILURE } from "../../actions/view";
import { useAppDispatch } from "../../hooks";
import styles from "../../styles/View/View.module.css";
import { EmptyDocForPdfViewer } from "../../../public/icons/index";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url,
).toString();

const ErrorDiv = (
  <div style={{ width: "100%", height: "60vh", alignItems: "center", justifyContent: "center" }}>
    <div className={styles.emptyDocsIcon}>
      <EmptyDocForPdfViewer />
      <p>Не вдалося відобразити документ.</p>
      <p>Зверніться до служби підтримки</p>
    </div>
  </div>
);

interface PdfViewerProps {
  url: string;
  width: number;
  height: number;
  scale: number;
  currentPage: number;
  onLoadSuccess: (numPages: number) => void;
  onScaleChange: (scale: number) => void;
}

function getTouchDist(touches: TouchList): number {
  return Math.hypot(
    touches[0].clientX - touches[1].clientX,
    touches[0].clientY - touches[1].clientY,
  );
}

export default function PdfViewer(props: PdfViewerProps) {
  const auth = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const httpHeaders = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};

  const file = useMemo(() => ({ url: props.url, httpHeaders }), [props.url]);

  const containerRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(props.scale);
  scaleRef.current = props.scale;
  const onScaleChangeRef = useRef(props.onScaleChange);
  onScaleChangeRef.current = props.onScaleChange;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // pinch-to-zoom via native touch events (passive: false дозволяє preventDefault)
    let startDist = 0;
    let startScale = 0;
    let isPinching = false;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        isPinching = true;
        startDist = getTouchDist(e.touches);
        startScale = scaleRef.current;
        e.preventDefault();
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && isPinching) {
        e.preventDefault();
        const ratio = getTouchDist(e.touches) / startDist;
        const next = Math.round(startScale * ratio);
        onScaleChangeRef.current(Math.min(200, Math.max(30, next)));
      }
    };

    const onTouchEnd = () => { isPinching = false; };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);

    // wheel zoom (Ctrl+scroll / trackpad pinch на десктопі)
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const next = Math.round(scaleRef.current + -e.deltaY * 0.3);
      onScaleChangeRef.current(Math.min(200, Math.max(30, next)));
    };

    // gesturechange для Safari на macOS
    let gestureStartScale = 0;
    const onGestureStart = (e: Event) => {
      e.preventDefault();
      gestureStartScale = scaleRef.current;
    };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const scale = (e as unknown as { scale: number }).scale;
      onScaleChangeRef.current(Math.min(200, Math.max(30, Math.round(gestureStartScale * scale))));
    };

    document.addEventListener("wheel", onWheel, { passive: false });
    document.addEventListener("gesturestart", onGestureStart);
    document.addEventListener("gesturechange", onGestureChange);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("wheel", onWheel);
      document.removeEventListener("gesturestart", onGestureStart);
      document.removeEventListener("gesturechange", onGestureChange);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: PDFDocumentProxy) => {
    props.onLoadSuccess(numPages);
  };
  const onDocumentLoadError = () => {
    dispatch({ type: DOCUMENT_VIEW_FAILURE });
  };

  return props.url ? (
    <div ref={containerRef} style={{ touchAction: "pan-y" }}>
      <Document
        loading={SkeletonLoader}
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        error={ErrorDiv}
      >
        <Page
          key={`page_${props.currentPage}`}
          pageNumber={props.currentPage}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          width={props.width - 16}
          scale={props.scale / 100}
          loading={SkeletonLoader}
          error={SkeletonLoader}
        />
      </Document>
    </div>
  ) : (
    <SkeletonLoader />
  );
}
